import {
  IonContent,
  IonPage,
  useIonViewDidEnter,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from '@ionic/react';
import React, { useLayoutEffect, useMemo, useState } from 'react';
import './Default.css';
import AppPageHeader from '../components/AppPageHeader';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router';
import useEnhancedAuth from '../EnhancedAuth';
import CardCurrentValue from '../usecases/cardvalue/CardCurrentValue';
import AppSeparator from '../components/AppSeparator';
import CardValueSelect from '../usecases/shared/CardValueSelect';
import {
  apiApplePay,
  apiGetPaymentMethods,
  apiGooglePay,
  apiPurchase,
} from '../services/api';
import { Farecard, PaymentMethod } from '../services/types/apitypes';
import CardAddValueButton from '../usecases/cardvalue/CardAddValueButton';
import {
  getAppError,
  getAppSuccess,
  getUserMessageOrValidationError,
} from '../utils/messages';
import PaymentAddButton from '../usecases/payments/PaymentAddButton';
import PaymentsList, {AdditionalPaymentMethod, AdditionalPaymentMethods} from '../usecases/payments/PaymentsList';
import PaymentAddProcess from '../usecases/shared/PaymentAddProcess';
import PlatformPayButton from '../usecases/payments/PlatformPayButton';
import PaymentMethodSelector from '../usecases/payments/PaymentMethodSelector';
import PaymentTotal from '../usecases/payments/PaymentTotal';
import {
  pickInitialPaymentMethod,
  rememberPaymentMethod,
} from '../usecases/payments/preferredPayment';
import { cardsLoadAndSelect } from '../utils/cards';
import {
  CallbackFnProps,
  defaultConfigure, getAdditionalPaymentMethods,
  getFieldsForPlatform,
  useCollectJS,
} from '../hooks/usecollectJS';
import { isPlatformButtonEnabled } from '../utils/config';
import { LoadingController } from '../utils/LoadingController';
import { useLoading } from '../context/LoadingContext';
import { useToaster } from '../hooks/useToaster';

// Unique selector so this page's platform button never collides with another
// page's that Ionic has left mounted in the DOM.
const PLATFORM_BUTTON_ID = 'value-platform-button';
const fields = getFieldsForPlatform('#' + PLATFORM_BUTTON_ID);
// Platform + build-time flag only, so this is constant for the life of the app.
const HAS_ADDITIONAL_PAYMENTS = getAdditionalPaymentMethods().length > 0;

const CardValuePage: React.FC = () => {
  const auth = useEnhancedAuth();
  const [t] = useTranslation('global');
  const router = useHistory();
  const [present] = useToaster();

  const minimumValue = 3;
  const maximumValue = 500;
  const DEFAULT_VALUE = 3;

  const { cardid } = useParams<{ cardid: string }>();

  const { isLoading } = useLoading();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [block, setBlock] = useState<string>('addvalue');

  const [selectedValue, setSelectedValue] = useState<number>(DEFAULT_VALUE);

  const [selectedCard, setSelectedCard] = useState<Farecard | undefined>(
    undefined,
  );
  const [cards, setCards] = useState<Farecard[] | undefined>(undefined);

  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    PaymentMethod | AdditionalPaymentMethod | undefined
  >(undefined);

  const [isPageActive, setIsPageActive] = useState(false);

  useIonViewWillEnter(() => {
    setSelectedValue(DEFAULT_VALUE);
  });

  const collectJsConfig = useMemo(
    () => ({
      ...defaultConfigure,
      price: selectedValue.toFixed(2),
      currency: 'USD',
      country: 'US',
      fields,
      callback: async (e: CallbackFnProps) => {
        try {
          LoadingController.show();
          setIsSubmitting(true);

          if ('applePay' in fields) {
            await apiApplePay(
              {
                products: [],
                topUps: [
                  {
                    amount: selectedValue * 100,
                    cardId: cardid,
                  },
                ],
                cardNetwork: e.wallet.cardNetwork,
                encryptedPaymentData: "",
                paymentToken: e.token
              },
              auth,
            );
          } else {
            await apiGooglePay(
              {
                isStoredValuePayment: false,
                partialPayment: [],
                products: [],
                topUps: [
                  {
                    amount: selectedValue * 100,
                    cardId: cardid,
                  },
                ],
                paymentToken: e.token,
              },
              auth,
            );
          }
          rememberPaymentMethod('applePay' in fields ? 'APPLE' : 'GOOGLE');
          present(getAppSuccess(t('cardvalue.loadsuccess')));
          await reloadCardData();
        } catch (error) {
          console.error('Error adding value ', error);
          const apiError = getUserMessageOrValidationError(error);
          if (typeof apiError === 'string') {
            present(getAppError(apiError));
          } else {
            present(getAppError(t('cardvalue.loadnosuccess')));
          }
        } finally {
          setIsSubmitting(false);
          LoadingController.hide();
        }
      },
    }),
    [selectedValue, auth, t],
  );

  const collectJs = useCollectJS(collectJsConfig);

  useLayoutEffect(() => {
    if (collectJs) {
      collectJs.configure();
    }
  }, [collectJs, block, isLoading]);

  useIonViewDidEnter(() => {
    setIsPageActive(true);
  });
  useIonViewWillLeave(() => {
    setIsPageActive(false);
  });

  const selectPaymentMethod = (p: PaymentMethod | AdditionalPaymentMethod) => {
    setSelectedPaymentMethod(p);
    setBlock('addvalue');
  };

  const returnFromAddPayment = async () => {
    const payments = await loadPayments();
    // Nothing to choose between — the user cancelled the form, has no saved
    // cards, and wallets are off. Go back where they started instead of
    // showing an empty picker.
    const nothingToChoose = (payments?.length ?? 0) === 0 && !HAS_ADDITIONAL_PAYMENTS;
    setBlock(nothingToChoose ? 'addvalue' : 'selectpayment');
  };

  const chosePayments = () => {
    setBlock('selectpayment');
  };

  const addPaymentMethod = () => {
    setBlock('addpayment');
  };

  // With wallets off there is nothing to choose between: the user has no
  // saved cards (or selectedPaymentMethod would be set) and no wallets, so
  // send them straight to the card form as before.
  const choosePaymentOrAdd = () =>
    HAS_ADDITIONAL_PAYMENTS ? chosePayments() : addPaymentMethod();

  // dont use useEffect, it does not trigger the second time a page with the same parameter is loaded.
  // we want the page to reload the current data every time it is opened
  useIonViewWillEnter(() => {
    reloadCardData().then(() => console.log('Loaded cards'));
    loadPayments().then(() => console.log('Loaded payments'));
  }, [auth.isLoading]);

  const reloadCardData = async () => {
    await cardsLoadAndSelect(cardid, setCards, setSelectedCard, auth);
  };

  const loadPayments = async () => {
    LoadingController.show();
    try {
      const response = await apiGetPaymentMethods(auth);
      setPaymentMethods(response.data);
      const initial = pickInitialPaymentMethod(response.data, getAdditionalPaymentMethods());
      if (initial) {
        setSelectedPaymentMethod(initial);
      }
      return response.data;
    } catch (error) {
      console.log('Could not load payments.');
    } finally {
      LoadingController.hide();
    }
  };

  const goBack = () => {
    router.push('/cards');
  };

  const getValueaddValidationError = () => {
    if (selectedValue < minimumValue) {
      return t('cardvalue.errorminvalue', { v: minimumValue });
    }
    const currentCardValue = selectedCard?.balance || 0;
    if (selectedValue + currentCardValue > maximumValue) {
      return t('cardvalue.errormaxvalue', { v: maximumValue });
    }
    if (!selectedCard) {
      return t('cardvalue.invalidcard');
    }
    if (!selectedPaymentMethod) {
      return t('cardvalue.errorpayment');
    }
    return false;
  };

  const addValueFinish = async (
    paymentMethod: PaymentMethod | AdditionalPaymentMethod,
    value: number,
  ) => {
    if (isSubmitting) {
      return;
    }

    if (typeof paymentMethod === "string") return;

    const validationError = getValueaddValidationError();
    if (validationError !== false) {
      present(getAppError(validationError));
      return;
    }

    try {
      LoadingController.show();
      setIsSubmitting(true);

      await apiPurchase(
        {
          isStoredValuePayment: false,
          partialPayment: [],
          paymentMethodId: paymentMethod.id,
          products: [],
          topUps: [
            {
              amount: value * 100,
              cardId: cardid,
            },
          ],
        },
        auth,
      );
      rememberPaymentMethod(paymentMethod);
      present(getAppSuccess(t('cardvalue.loadsuccess')));
      await reloadCardData();
    } catch (error) {
      console.error('Error adding value ', error);
      const apiError = getUserMessageOrValidationError(error);
      if (typeof apiError === 'string') {
        present(getAppError(apiError));
      } else {
        present(getAppError(t('cardvalue.loadnosuccess')));
      }
    } finally {
      setIsSubmitting(false);
      LoadingController.hide();
    }
  };

  if (!selectedCard) {
    return <IonPage />;
  }

  return (
    <IonPage id="farecardvalue-page">
      <>
        {block === 'addpayment' && (
          <PaymentAddProcess
            isPageActive={isPageActive}
            callback={returnFromAddPayment}
          />
        )}
        {block === 'selectpayment' && (
          <>
            <AppPageHeader
              isPageActive={isPageActive}
              back={() => setBlock('addvalue')}
              title={t('cardvalue.title')}
            ></AppPageHeader>
            <IonContent fullscreen className="ion-padding default-content">
              <PaymentsList
                payments={paymentMethods}
                additionalPayments={getAdditionalPaymentMethods()}
                selectPayment={selectPaymentMethod}
              />
              <AppSeparator />
              <PaymentAddButton click={addPaymentMethod} />
            </IonContent>
          </>
        )}
        {block === 'addvalue' && (
          <>
            <AppPageHeader
              isPageActive={isPageActive}
              back={() => goBack()}
              title={t('cardvalue.title')}
            ></AppPageHeader>
            <IonContent fullscreen className="ion-padding default-content">
              <CardCurrentValue card={selectedCard} />
              <CardValueSelect
                minimum={minimumValue}
                maximum={maximumValue}
                currentCardValue={selectedCard.balance || 0}
                selectedValue={selectedValue}
                setSelectedValue={setSelectedValue}
              />
              <AppSeparator />
              <>
                {selectedPaymentMethod ? (
                  <>
                    <PaymentMethodSelector
                      click={chosePayments}
                      payment={selectedPaymentMethod}
                    />
                    <PaymentTotal amount={selectedValue} />
                    {typeof selectedPaymentMethod === "string" ? (<>
                      {isPlatformButtonEnabled() && (
                        <PlatformPayButton id={PLATFORM_BUTTON_ID} />
                      )}
                    </>) :
                    <CardAddValueButton
                      proceed={() =>
                        addValueFinish(selectedPaymentMethod, selectedValue)
                      }
                      isValid={getValueaddValidationError() === false}
                    />}
                  </>
                ) : (
                  <>
                    <PaymentAddButton
                      click={choosePaymentOrAdd}
                      labelKey={HAS_ADDITIONAL_PAYMENTS ? 'payments.choose' : 'payments.add'}
                    />
                  </>
                )}
              </>
            </IonContent>
          </>
        )}
      </>
    </IonPage>
  );
};

export default CardValuePage;
